/**
 * Wave 29 Chunk 4 — Announcement card (feed item).
 *
 * Auto-marks-as-read when it enters view (IntersectionObserver). HR can
 * edit/delete via the actions slot in the parent.
 */
import { useEffect, useRef } from "react";
import { Megaphone, Pin, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";

export default function AnnouncementCard({ ann, canEdit, onEdit, onDelete }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || ann.read) return;
    const io = new window.IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          api.post(`/team/announcements/${ann.id}/read`).catch(() => {});
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.6 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ann.id, ann.read]);

  return (
    <div
      ref={ref}
      className={`hv-card p-4 ${!ann.read ? "border-[#1FB6C1]/30" : ""}`}
      data-testid={`announcement-${ann.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#1FB6C1]/15 text-[#1FB6C1] flex items-center justify-center shrink-0">
            <Megaphone className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-display text-base font-bold">{ann.title}</h3>
              {ann.pinned && (
                <span className="text-[9px] uppercase tracking-wider inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30">
                  <Pin className="w-3 h-3" /> Pinned
                </span>
              )}
              {!ann.read && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/30">
                  New
                </span>
              )}
            </div>
            <div className="text-[10px] text-[#647087] mt-0.5">
              by {ann.created_by_name} · {new Date(ann.created_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}
              {ann.audience !== "all" && <span> · for {ann.audience}</span>}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onEdit?.(ann)}
              className="hv-btn hv-btn-ghost text-[10px] px-2 py-1"
              data-testid={`announcement-edit-${ann.id}`}
              aria-label="Edit"
            >
              <Pencil className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => onDelete?.(ann)}
              className="hv-btn hv-btn-ghost text-[10px] px-2 py-1 text-red-300"
              data-testid={`announcement-delete-${ann.id}`}
              aria-label="Delete"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-[#cfd6e3] mt-2 whitespace-pre-wrap leading-relaxed">{ann.body}</p>
    </div>
  );
}
